import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type { CaseItem } from '../data/types';

interface UseBulkReassignParams {
  /** Per-case mutation that intentionally skips per-mutation invalidation —
   *  this hook coalesces invalidations into a single end-of-batch refetch. */
  reassignOne: (caseId: string, newAgentId: string) => Promise<void>;
  /** Called once at the end of the batch with the set of program ids touched. */
  invalidatePrograms: (programIds: Iterable<string>) => void;
}

/** Bounded parallelism — large selections finish in a few round-trips without
 *  flooding the backend, while keeping the optimistic-update cache coherent
 *  (each mutation settles before its worker slot is reused). */
const CONCURRENCY = 4;

export interface BulkReassignResult {
  succeeded: number;
  failed: number;
  programsTouched: Set<string>;
}

interface RunBulkReassignOpts {
  cases: CaseItem[];
  newAgentId: string;
  reassignOne: (caseId: string, newAgentId: string) => Promise<void>;
  invalidatePrograms: (programIds: Iterable<string>) => void;
  onSuccessToast?: (succeeded: number) => void;
  onPartialToast?: (succeeded: number, total: number, failed: number) => void;
  /** Override the default `CONCURRENCY` for testing. */
  concurrency?: number;
}

/**
 * Pure orchestration core for the per-aggregate-cell bulk reassign. Not a
 * hook — used inside `useBulkReassign` and importable for unit tests.
 *
 *   - Worker pool of size `concurrency` drains `cases` and calls `reassignOne`.
 *     Per-mutation invalidation is suppressed by the caller's `reassignOne`
 *     (it passes `suppressInvalidate: true` to `useReassignCase`).
 *   - Tracks the deduped set of programs touched by successful reassigns; calls
 *     `invalidatePrograms` exactly once at the end so we coalesce N parallel
 *     invalidations into one prefix-invalidate per program.
 *   - Decides which summary toast to surface:
 *       all succeeded → `onSuccessToast`
 *       partial       → `onPartialToast`
 *       all failed    → neither (per-failure toasts are emitted by
 *                                 `useReassignCase` itself)
 *
 * Future scope: when the backend exposes a true bulk-reassign endpoint we
 * can drop the worker pool and issue a single request.
 */
export async function runBulkReassign({
  cases,
  newAgentId,
  reassignOne,
  invalidatePrograms,
  onSuccessToast,
  onPartialToast,
  concurrency = CONCURRENCY,
}: RunBulkReassignOpts): Promise<BulkReassignResult> {
  const programsTouched = new Set<string>();
  if (!newAgentId || cases.length === 0) {
    return { succeeded: 0, failed: 0, programsTouched };
  }
  let succeeded = 0;
  let failed = 0;
  const queue = [...cases];
  const worker = async () => {
    while (queue.length) {
      const item = queue.shift();
      if (!item) return;
      try {
        await reassignOne(item.id, newAgentId);
        if (item.programId) programsTouched.add(item.programId);
        succeeded += 1;
      } catch {
        failed += 1;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, cases.length) }, () => worker()));
  invalidatePrograms(programsTouched);

  if (failed === 0) {
    onSuccessToast?.(succeeded);
  } else if (succeeded > 0) {
    onPartialToast?.(succeeded, cases.length, failed);
  }
  return { succeeded, failed, programsTouched };
}

/**
 * React hook wrapper around `runBulkReassign`. Adds the `inFlight` state
 * and wires `sonner` toasts; the orchestration itself lives in the pure
 * function above so it can be unit-tested without React.
 */
export function useBulkReassign({ reassignOne, invalidatePrograms }: UseBulkReassignParams) {
  const [inFlight, setInFlight] = useState(false);

  const run = useCallback(
    async (cases: CaseItem[], newAgentId: string) => {
      setInFlight(true);
      try {
        return await runBulkReassign({
          cases,
          newAgentId,
          reassignOne,
          invalidatePrograms,
          onSuccessToast: n => toast.success(`Reassigned ${n} case${n === 1 ? '' : 's'}.`),
          onPartialToast: (s, total, f) =>
            toast.warning(`Reassigned ${s} of ${total}. ${f} failed.`),
        });
      } finally {
        setInFlight(false);
      }
    },
    [reassignOne, invalidatePrograms],
  );

  return { run, inFlight };
}
